import Image from "next/legacy/image";
import Link from 'next/link'
import styled from 'styled-components'

import grabColor from 'src/utils/grabColor'
import { H2Alt } from 'src/styles/Typography'
import { StyledAnchor } from 'src/styles/Button'
import { Box } from 'src/styles/Container'
import RankIcon from 'src/components/icons/Rank'
import StarRating from 'src/components/StarRating'

const listOfMedicareParts = ['Hospital', 'Medical', 'Advantage', 'Prescription']
const listOfSupplementPlans = ['A', 'B', 'C', 'D', 'F', 'G', 'K', 'L', 'M', 'N']

function VerticalProviderBox({ className = '', provider, showIcon = false }) {
  const url = `/${encodeURIComponent(
    provider?.vertical?.slug,
  )}/reviews/${encodeURIComponent(provider?.slug)}`

  const metaDataType = getMetaType(provider)

  // console.log('provider?.metaData', provider?.metaData)

  return (
    <div className={className}>
      <CardInfo>
        <CardTitle>
          {showIcon && <RankIcon />}

          <Link href={url}>

            <H2Alt>{provider?.title}</H2Alt>

          </Link>
        </CardTitle>

        <InfoRowsByVertical
          metaDataType={metaDataType}
          url={url}
          meta={provider?.metaData || {}}
        />
      </CardInfo>

      <CardSidebar>
        <VerticalProviderSidebar
          metaDataType={metaDataType}
          url={url}
          meta={provider?.metaData || {}}
          provider={provider}
        />
      </CardSidebar>
    </div>
  );
}

const VerticalProviderSidebar = styled(
  ({ className = '', url, meta, metaDataType, provider }) => {
    const rating = meta?.policyScoutRating

    return (
      <div className={className}>
        {provider?.logo?.url && (
          <Image
            src={provider?.logo?.url}
            quality={40}
            width="160"
            height="50"
            alt={provider?.logo?.description || ''}
            objectFit="contain"
          />
        )}

        <Link href={url}>View PolicyScout Review</Link>

        {rating && (
          <Rating>
            <StarRating rating={rating} size="24" />
            <H2Alt>{rating}</H2Alt>
          </Rating>
        )}

        <HR />

        <SidebarRowsByVertical metaDataType={metaDataType} provider={provider} />

        <Link href={`/apply/${provider?.vertical?.slug}`} passHref legacyBehavior>
          <StyledAnchor size="large">Get Quotes</StyledAnchor>
        </Link>
      </div>
    );
  },
)``

/**
 * Sidebar Rows by Vertical
 */

const SidebarRowsByVertical = styled(({ className = '', metaDataType, meta }) => {
  // switch (metaDataType) {
  //   case 'life':
  //     return null
  //     break

  //   case 'medicare':
  //     return (
  //       <SidebarRows className={className}>
  //         <SidebarRow>
  //           <InnerBox>
  //             <strong>
  //               0.0 <StarIcon />
  //             </strong>
  //           </InnerBox>
  //         </SidebarRow>

  //         <SidebarRow>
  //           <InnerBox>XYZ Customer Rating</InnerBox>
  //           <InnerBox>
  //             <strong>0/10</strong>
  //           </InnerBox>
  //         </SidebarRow>
  //       </SidebarRows>
  //     )
  //     break
  // }
  return null
})``

/**
 * Info Rows by Vertical
 */

const InfoRowsByVertical = styled(({ metaDataType, meta }) => {
  switch (metaDataType) {
    case 'medicare':
      return (
        <InfoRows>
          {meta?.medicareParts?.length && (
            <InfoRow>
              <InfoLabel>Medicare Parts</InfoLabel>
              <InfoContent>
                <ConnectedBoxes
                  items={meta?.medicareParts}
                  listOfItems={listOfMedicareParts}
                />
              </InfoContent>
            </InfoRow>
          )}

          {meta?.supplementPlans?.length && (
            <InfoRow>
              <InfoLabel>Supplement Plans</InfoLabel>
              <InfoContent>
                <ConnectedBoxes
                  items={meta?.supplementPlans}
                  listOfItems={listOfSupplementPlans}
                />
              </InfoContent>
            </InfoRow>
          )}

          {meta?.cmsStarRating && (
            <InfoRow>
              <InfoLabel>CMS Star Rating</InfoLabel>
              <InfoContent>{meta?.cmsStarRating}</InfoContent>
            </InfoRow>
          )}

          {meta?.excludedState?.length && (
            <InfoRow>
              <InfoLabel>Excluded States</InfoLabel>
              <InfoContent>
                <ExcludedStates excludedStates={meta?.excludedState} />
              </InfoContent>
            </InfoRow>
          )}
        </InfoRows>
      )
      break
    case 'health':
      return (
        <InfoRows>
          {meta?.financialStrengthRatings && (
            <InfoRow>
              <InfoLabel>AM Best Rating</InfoLabel>
              <InfoContent>{meta?.financialStrengthRatings}</InfoContent>
            </InfoRow>
          )}

          {meta?.customerServiceRating && (
            <InfoRow>
              <InfoLabel>Customer Service Rating</InfoLabel>
              <InfoContent>{meta?.customerServiceRating}</InfoContent>
            </InfoRow>
          )}

          {meta?.coverageAreaExcludedStates?.length && (
            <InfoRow>
              <InfoLabel>Excluded States</InfoLabel>
              <InfoContent>
                <ExcludedStates excludedStates={meta?.coverageAreaExcludedStates} />
              </InfoContent>
            </InfoRow>
          )}
        </InfoRows>
      )
      break

    case 'life':
      return (
        <InfoRows>
          {meta?.financialStrengthRatings && (
            <InfoRow>
              <InfoLabel>AM Best Rating</InfoLabel>
              <InfoContent>{meta?.financialStrengthRatings}</InfoContent>
            </InfoRow>
          )}

          {meta?.sampleCostTermLifeInsurance || meta?.sampleCostPermanentLifeInsurance ? (
            <InfoRow>
              <InfoLabel>Sample Costs</InfoLabel>
              <InfoContent>
                {meta?.sampleCostTermLifeInsurance && (
                  <span>
                    <strong>Term Life Insurance:</strong>{' '}
                    {meta?.sampleCostTermLifeInsurance}{' '}
                  </span>
                )}

                {meta?.sampleCostPermanentLifeInsurance && (
                  <span>
                    <strong>Permanent Life:</strong>{' '}
                    {meta?.sampleCostPermanentLifeInsurance}
                  </span>
                )}
              </InfoContent>
            </InfoRow>
          ) : null}

          {meta?.customerSatisfactionRating && (
            <InfoRow>
              <InfoLabel>Customer Satisfaction</InfoLabel>
              <InfoContent>{meta?.customerSatisfactionRating}</InfoContent>
            </InfoRow>
          )}
        </InfoRows>
      )

    case 'auto':
      return (
        <InfoRows>
          {meta?.financialStrengthRatings && (
            <InfoRow>
              <InfoLabel>AM Best Rating</InfoLabel>
              <InfoContent>{meta?.financialStrengthRatings}</InfoContent>
            </InfoRow>
          )}

          {meta?.customerSatisfactionRating && (
            <InfoRow>
              <InfoLabel>Customer Satisfaction</InfoLabel>
              <InfoContent>{meta?.customerSatisfactionRating}</InfoContent>
            </InfoRow>
          )}

          {meta?.sampleCostMonthlyRate && (
            <InfoRow>
              <InfoLabel>Sample Costs</InfoLabel>
              <InfoContent>{meta?.sampleCostMonthlyRate}</InfoContent>
            </InfoRow>
          )}
        </InfoRows>
      )

    case 'home':
      return (
        <InfoRows>
          {meta?.sampleCostAverageAnnualRate && (
            <InfoRow>
              <InfoLabel>Sample Costs</InfoLabel>
              <InfoContent>{meta?.sampleCostAverageAnnualRate}</InfoContent>
            </InfoRow>
          )}

          {meta?.customerSatisfactionRating && (
            <InfoRow>
              <InfoLabel>AM Best Rating</InfoLabel>
              <InfoContent>{meta?.customerSatisfactionRating}</InfoContent>
            </InfoRow>
          )}
        </InfoRows>
      )

    case 'pet':
      return (
        <InfoRows>
          {meta?.maximumEnrollAge && (
            <InfoRow>
              <InfoLabel>Minimum Enroll Age</InfoLabel>
              <InfoContent>{meta?.maximumEnrollAge}</InfoContent>
            </InfoRow>
          )}

          {meta?.coverageAreaExcludedStates?.length && (
            <InfoRow>
              <InfoLabel>Coverage Area</InfoLabel>
              <InfoContent>
                <ExcludedStates excludedStates={meta?.coverageAreaExcludedStates} />
              </InfoContent>
            </InfoRow>
          )}

          {meta?.sampleCostAnnualCoverage && (
            <InfoRow>
              <InfoLabel>Sample Cost Annual Coverage</InfoLabel>
              <InfoContent>{meta?.sampleCostAnnualCoverage}</InfoContent>
            </InfoRow>
          )}

          {meta?.customerSatisfactionRating && (
            <InfoRow>
              <InfoLabel>Customer Satisfaction Rating</InfoLabel>
              <InfoContent>{meta?.customerSatisfactionRating}</InfoContent>
            </InfoRow>
          )}

          <InfoRow>
            <InfoLabel>Money Back Guarantee</InfoLabel>
            <InfoContent>{meta?.moneyBackGuarantee ? 'Yes' : 'No'}</InfoContent>
          </InfoRow>
        </InfoRows>
      )
  }

  return null
})``

/**
 * Styles
 */

const SidebarRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  padding: 1em 0;
`
// const SidebarRow = styled.div`
//   display: flex;
//   width: 100%;
//   max-width:
//   align-items: center;
//   justify-content: space-between;
// `

const Rating = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const InfoRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`
const InfoRow = styled.div`
  display: flex;
  align-items: center;
`
const InfoLabel = styled.div`
  flex: 1;
  padding-right: 1em;
`
const InfoContent = styled.div`
  flex: 5;
`

const HR = styled.hr`
  border: 2px solid #ffffff;
  width: 90%;
`

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;

  a {
    text-decoration: underline;
    text-decoration-color: ${grabColor('heading')};
  }
`

const CardInfo = styled.div`
  background-color: #ffffff;
  padding: 1em;

  ${CardTitle} {
    padding-bottom: 2em;
  }
`
const CardSidebar = styled.div`
  background-color: ${grabColor('backgroundMid')};

  ${VerticalProviderSidebar} {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding: 1em;
    gap: 1em;
  }

  ${Rating} {
    max-width: 220px;
  }

  ${StyledAnchor} {
    width: 100%;
    max-width: 220px;
    color: white;
  }
`

const StyledVerticalProviderBox = styled(VerticalProviderBox)`
  display: flex;
  width: 100%;
  filter: drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.1))
    drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.06));
  border-radius: 2px;
  margin-bottom: 2.5rem;

  a {
    color: ${grabColor('action.DEFAULT')};
  }

  ${CardInfo} {
    flex: 3;
  }

  ${CardSidebar} {
    flex: 1;
  }
`

const StyledBoxes = styled.div`
  display: flex;
  gap: 1em;
  color: ${({ filled }) => {
    if (filled) {
      return '#1F505C'
    }

    return '#6B7280'
  }};

  > * {
    ${({ filled }) => {
      if (filled) {
        return `background-color: #F5FDFF;`
      }

      return `border: 1px solid #e3ebed;`
    }}

    display: block;
    padding: 0.65em 1em;
  }
`

const StyledConnectedBoxes = styled(StyledBoxes)`
  display: flex;
  gap: 0;

  > * {
    border-left: 0;
    color: #088ab2;

    &:first-child {
      border-left: 1px solid #e3ebed;
    }
  }
`

const StyledInnerBox = styled(Box)`
  ${({ isActive = false }) => {
    if (isActive) {
      return `
        color: #088AB2;
        background-color: #FFF;
      `
    }

    return `
      color:#6B7280;
      background-color: #F1F3F4;
    `
  }};
`

const ExcludedStates = styled(({ excludedStates = [] }) => {
  if (!excludedStates || !excludedStates?.length) {
    return 'No Excluded States'
  }

  if (excludedStates.length >= 8) {
    return (
      <>
        <strong>Exceptions in:</strong> {`${excludedStates?.join?.(', ')}`}
      </>
    )
  }

  return (
    <>
      <strong>Most of the US - exceptions in:</strong> {`${excludedStates?.join?.(', ')}`}
    </>
  )
})``

const ConnectedBoxes = ({ items, listOfItems = [], className = '' }) => {
  const isActive = (item) => items?.includes?.(item) || false

  return (
    <div className={className}>
      <StyledConnectedBoxes>
        {listOfItems.map((item) => (
          <StyledInnerBox key={item} isActive={isActive(item)}>
            {item}
          </StyledInnerBox>
        ))}
      </StyledConnectedBoxes>
    </div>
  )
}

/**
 * Helpers
 */

export function getRowsByMetaDataType(metaDataType, provider) {
  switch (metaDataType) {
    case 'life':
      return [
        {
          type: 'fsr',
          title: 'Financial Strength Ratings',
          value: provider?.metaData?.financialStrengthRatings,
        },
      ]
    case 'medicare':
      return []
    case 'health':
      return []
    case 'auto':
      return []
    case 'home':
      return []
    case 'pet':
      return []
  }

  return []
}

export function getMetaType(provider) {
  const meta = provider?.metaData
  const metaDataType = meta?.__typename

  switch (metaDataType) {
    case 'LifeInsuranceProviderMetaData':
      return 'life'
    case 'MedicareProviderMetaData':
      return 'medicare'
    case 'HealthInsuranceProviderMetaData':
      return 'health'
    case 'AutoInsuranceProviderMetaData':
      return 'auto'
    case 'HomeInsuranceProviderMetaData':
      return 'home'
    case 'PetInsuranceProviderMetaData':
      return 'pet'
  }

  return 'unkown'
}

export default StyledVerticalProviderBox
